import { ChangeEvent, Dispatch, FC, SetStateAction } from "react";
import { TextField, Box, Button, Select, MenuItem, IconButton, Switch } from "@mui/material";
import { useTranslation } from "react-i18next";
import { EntryField, EntryTitle, EntryWrapper } from "../../../components/customStyles/form";
import IFloor from "../../../models/floor";
import { theme } from "../../../styles/themes/mainTheme";
import CloseIcon from '@mui/icons-material/Close';
import EditOnly from "../../../components/permissions/EditOnly";
import IAdminPageBuildingDetail from "../../../models/adminPage/adminPageBuildingDetail";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

interface FloorFormFieldsProps {
  floorState: IFloor
  setFloorState: Dispatch<
    SetStateAction<IFloor>
  >;
  hbscanMapURL: string;
  onFileChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: () => void;
  buildings: IAdminPageBuildingDetail[];
}

const FloorFormFields: FC<FloorFormFieldsProps> = ({
  floorState,
  setFloorState,
  hbscanMapURL,
  onFileChange,
  onRemoveFile,
  buildings
}) => {
  const { t } = useTranslation();

  const updateFloorState = <T extends keyof IFloor>(
    field: T,
    value: IFloor[T]
  ) => {
    setFloorState((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  return (
    <>
      <EntryWrapper>
        <EntryTitle>
          {t("table.floor.name")} *
        </EntryTitle>
        <EntryField>
          <TextField
            required={true}
            style={{ textAlign: "left", width: "100%" }}
            value={floorState.name}
            onChange={(e) => {
              updateFloorState(
                "name",
                e.target.value
              );
            }}
          />
        </EntryField>
      </EntryWrapper>

      <EntryWrapper>
        <EntryTitle>
          {t("table.floorNumber")} *
        </EntryTitle>
        <EntryField>
          <TextField
            required={true}
            style={{ textAlign: "left", width: "100%" }}
            type="number"
            value={floorState.floorNumber}
            onChange={(e) => {
              updateFloorState(
                "floorNumber",
                Number(e.target.value)
              );
            }}
          />
        </EntryField>
      </EntryWrapper>

      <EntryWrapper>
        <EntryTitle>{t("table.local.building")} *</EntryTitle>
        <EntryField>
          <Select
            required={true}
            value={floorState.buildingId}
            onChange={(e) =>
              updateFloorState(
                "buildingId",
                Number(e.target.value)
              )
            }
            sx={{ width: "100%", height: "2em" }}
            MenuProps={{
              style: { overflowY: "visible" },
            }}
          >
            {buildings && buildings.map(
              (building, index) =>
                <MenuItem
                  key={index}
                  value={building.id}
                >
                  {building.name}
                </MenuItem>
            )}
          </Select>
        </EntryField>
      </EntryWrapper>

      <EntryWrapper>
        <EntryTitle sx={{ flex: "1" }}>{t("table.floor.overtimeCalculation")}</EntryTitle>
        <Switch checked={floorState.isOvertimeCalculation} value={floorState.isOvertimeCalculation} onChange={(e, checked) => updateFloorState("isOvertimeCalculation", checked)}></Switch>
      </EntryWrapper>

      <EntryWrapper >
        <EntryTitle sx={{ width: "100%" }}>{t("table.hbscanMap")}</EntryTitle>
        <EntryField>
          {hbscanMapURL && (
            <Box sx={{ marginBottom: 2, width: "100%", position: "relative" }}>
              <EditOnly>
                <IconButton onClick={onRemoveFile} sx={{
                  position: "absolute",
                  right: 0,
                  top: 0,
                  color: theme.palette.error.main,
                  zIndex: 1
                }}>
                  <CloseIcon />
                </IconButton>
              </EditOnly>

              <img src={hbscanMapURL} alt="Image couldn't be displayed" style={{ display: "block", marginInline: "auto", width: "100%", height: "auto" }} />
            </Box>
          )}

          <Button variant="contained" component="label" sx={{ marginLeft: "1em" }}>
            {t("table.upload")}
            <input
              accept="image/png, image/jpeg, image/jpg"
              type="file"
              onChange={onFileChange}
              hidden
            />
          </Button>

        </EntryField>
      </EntryWrapper>
    </>
  );
};

export default FloorFormFields;
